import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { APPLICATION_API_BASE_URL } from '../services/api/configuration/config';
import {
  appendIncomingMessage,
  upsertConversationInCache
} from '../utils/whatsappInboxCache';
import { INBOX_SUMMARY_KEY } from './useInboxUnread';

const EVENTS_URL = `${APPLICATION_API_BASE_URL}/whatsapp/inbox/events`;
const HEARTBEAT_TIMEOUT_MS = 45000;
const MAX_BACKOFF_MS = 30000;

const streamSupported =
  typeof window !== 'undefined' &&
  typeof window.fetch === 'function' &&
  typeof ReadableStream !== 'undefined' &&
  typeof TextDecoder !== 'undefined';

function parseEventBlock(block) {
  const lines = block.split('\n');
  let eventType = 'message';
  const dataLines = [];
  for (const line of lines) {
    if (!line || line.startsWith(':')) continue; // heartbeat / comment
    if (line.startsWith('event:')) {
      eventType = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^\s/, ''));
    }
  }
  if (!dataLines.length) return null;
  try {
    return { eventType, data: JSON.parse(dataLines.join('\n')) };
  } catch {
    return null;
  }
}

/**
 * Fetch-based Server-Sent Events client for the WhatsApp Inbox.
 *
 * Native EventSource can't send the Authorization header (raw JWT), so this
 * streams the response body manually. Handles `conversation_updated` and
 * `message_created` events, ignores heartbeats, and auto-reconnects with
 * exponential backoff. Exposes a status string ('connecting' | 'connected'
 * | 'reconnecting' | 'unsupported') so a polling fallback can react (Phase 6).
 */
export function useInboxStream({ enabled = true, onConversationUpdated } = {}) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState(streamSupported ? 'connecting' : 'unsupported');
  const statusRef = useRef(status);
  const onConversationUpdatedRef = useRef(onConversationUpdated);

  useEffect(() => {
    onConversationUpdatedRef.current = onConversationUpdated;
  }, [onConversationUpdated]);

  const updateStatus = (next) => {
    statusRef.current = next;
    setStatus(next);
  };

  useEffect(() => {
    if (!enabled || !streamSupported) {
      if (!streamSupported) updateStatus('unsupported');
      return undefined;
    }

    let aborted = false;
    let controller = null;
    let retry = 0;
    let heartbeatTimer = null;
    let reconnectTimer = null;

    const clearHeartbeat = () => {
      if (heartbeatTimer) clearTimeout(heartbeatTimer);
      heartbeatTimer = null;
    };

    const armHeartbeat = () => {
      clearHeartbeat();
      heartbeatTimer = setTimeout(() => {
        // No data or heartbeat within the window -> treat as stale and reconnect.
        if (controller) controller.abort();
      }, HEARTBEAT_TIMEOUT_MS);
    };

    const dispatch = ({ eventType, data }) => {
      if (eventType === 'conversation_updated') {
        upsertConversationInCache(queryClient, data);
        onConversationUpdatedRef.current?.(data);
      } else if (eventType === 'message_created') {
        appendIncomingMessage(queryClient, data?.phone_number, data?.message);
      } else {
        return;
      }
      // Refresh the unread summary so the nav/tab badge stays live even when the
      // Inbox list isn't mounted (e.g. while on the Campaigns/Media tabs).
      queryClient.invalidateQueries({ queryKey: INBOX_SUMMARY_KEY });
    };

    const connect = async () => {
      if (aborted) return;
      controller = new AbortController();
      updateStatus(retry === 0 ? 'connecting' : 'reconnecting');

      try {
        const token = Cookies.get('token');
        const response = await fetch(EVENTS_URL, {
          method: 'GET',
          headers: { authorization: `${token}`, Accept: 'text/event-stream' },
          signal: controller.signal,
          cache: 'no-store'
        });

        if (!response.ok || !response.body) {
          throw new Error(`SSE HTTP ${response.status}`);
        }

        retry = 0;
        updateStatus('connected');
        armHeartbeat();

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          armHeartbeat();
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

          let sepIndex;
          while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
            const block = buffer.slice(0, sepIndex);
            buffer = buffer.slice(sepIndex + 2);
            const parsed = parseEventBlock(block);
            if (parsed) dispatch(parsed);
          }
        }
        throw new Error('SSE stream closed');
      } catch (err) {
        if (aborted) return;
        clearHeartbeat();
        updateStatus('reconnecting');
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** retry) + Math.floor(Math.random() * 500);
        retry += 1;
        reconnectTimer = setTimeout(() => {
          if (!aborted) connect();
        }, delay);
      }
    };

    connect();

    return () => {
      aborted = true;
      clearHeartbeat();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (controller) controller.abort();
    };
  }, [enabled, queryClient]);

  return { status, isConnected: status === 'connected' };
}

export default useInboxStream;
